import { useRef, useEffect, useState, useCallback, useMemo } from 'react';

import { useApp } from '../../../../app/AppContext.jsx';
import { useHero } from '../../context/HeroContext.jsx';
import { useCatalog } from '../../../catalog/context/CatalogContext.jsx';

import { useI18n } from '../../../../shared/i18n/useI18n.js';
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js';

import { useSvgBuildingInteraction } from '../../../../shared/hooks/useSvgBuildingInteraction.js';
import { useSvgProductInteraction } from '../../../../shared/hooks/useSvgProductInteraction.js';
import { useSvgTierInteraction } from '../../../../shared/hooks/useSvgTierInteraction.js';
import { FETCH_STATUS } from '../../../../shared/constants/fetchStatus.js';
import { HERO_OUTLINE } from '../../../../shared/constants/heroOutline.js';
import { HERO_VIEW } from '../../../../shared/constants/heroView.js';

import { useVisualizationSvg } from '../../hooks/useVisualizationSvg.js';
import { useHeroSectionNavigation } from '../../hooks/useHeroSectionNavigation.js';
import {
	classifyVisPathIds,
	normalizeVisPathIds,
} from '../../utils/visPathNormalizer.js';
import { buildVisualizationProductCandidates } from '../../utils/visPreviewAdapter.js';
import { buildVisualizationOutlineCandidates } from '../../utils/outlinePreviewAdapter.js';

import { ButtonHero } from '../ButtonHero/ButtonHero.jsx';
import { PreviewOverlay } from '../PreviewOverlay/PreviewOverlay.jsx'; //move to shared/
import { ThreeDModel } from '../../../../shared/ui/icons/ThreeDModel.jsx';

import styles from './VisualizationView.module.scss';
import { HeroCtaButton } from '../HeroButtonCta/HeroButtonCta.jsx';

const CTA_ONBOARDING_DELAY_MS = 550;

/**
 * VisualizationView
 *
 * Renders the static site visualization. Unlike ModelView, all interactive
 * paths are embedded directly inside `main.svg` — there are no separate
 * `outlines_*.svg` files. Interaction listeners are wired directly on the
 * container that holds the injected SVG markup.
 *
 * main.svg uses a flat structure: all paths (buildings, tiers, apartments)
 * are top-level `<path id="...">` elements. Path type is inferred from ID
 * format. Non-canonical integer-only tier IDs (e.g. "9") are normalized to
 * canonical `"{building}-{tier}"` format before interaction hooks run.
 *
 * TODO: Discuss with team — main.svg should adopt grouped structure
 *       (like outlines_*.svg) to eliminate ID-format guessing.
 *
 * Must be rendered inside `HeroContextProvider`, `AppContextProvider`, and `CatalogContextProvider`.
 */
export function VisualizationView() {
	// ── i18n ──────────────────────────────────────────────────────────────────
	const { t } = useI18n();
	const getProductsCategoryString = useProductStrings();

	// ── Context reads ─────────────────────────────────────────────────────────
	const {
		appCapabilities,
		windowData,
		isMobile,
		activeBuilding,
		activeTier,
		activeProduct,
	} = useApp();

	const { changeViewHandler, heroCapabilities } = useHero();

	const { 
    buildings, 
    colorMode,
    colorModeDeciles,
    catalogCapabilities, 
    filteredProducts,
    products, 
    tiers, 
  } =
		useCatalog();

	// ── Local state ───────────────────────────────────────────────────────────

	const [detectedOutlineLayers, setDetectedOutlineLayers] = useState([]);
	const [isCtaExpanded, setIsCtaExpanded] = useState(false);
	const [hasCtaHoverArmed, setHasCtaHoverArmed] = useState(false);

	const visSvgRef = useRef(null);
	const previewHandlersRef = useRef({
		handlePreviewChange: () => {},
		handleClosePreview: () => {},
	});
	const executeActionRef = useRef(() => {});

	const handlePreviewChange = useCallback((data) => {
		previewHandlersRef.current.handlePreviewChange(data);
	}, []);

	const handleRegisterPreviewHandler = useCallback((handlers) => {
		previewHandlersRef.current = handlers;
		executeActionRef.current = handlers.handleExecuteAction;
	}, []);

	const handleOutlineAction = useCallback((mode, data) => {
		executeActionRef.current(mode, data);
	}, []);

	// ── Derived state ─────────────────────────────────────────────────────────
	const dataUrl = windowData?.projectDataUrl ?? null;

	const { reservedUnitsAccessible, soldUnitsAccessible } = appCapabilities;

	const { areSoldOutBuildingsActive, areSoldOutTiersActive } =
		catalogCapabilities;

	const areSoldAccessible = soldUnitsAccessible; //TODO: should boolean constants start with has*, are*, is* ?
	const areReservedAccessible = reservedUnitsAccessible; //TODO: should boolean constants start with has*, are*, is* ?

	const { svgString: visSvgString, svgStatus: visSvgStatus } =
		useVisualizationSvg({
			dataUrl,
			isMobile,
		});

	// ── Outline mobile carousel (buildings / tiers / products mobile swipe) ────────────────────

	const getVisOutlineCandidates = useCallback(
		(outlineType) => {
			return buildVisualizationOutlineCandidates({
				containerEl: visSvgRef.current,
				activeOutline: outlineType,
				buildings: buildings ?? [],
				tiers: tiers ?? [],
			});
		},
		[buildings, tiers],
	);

	const getVisProductCandidates = useCallback(() => {
		return buildVisualizationProductCandidates({
			containerEl: visSvgRef.current,
			products: products ?? [],
		});
	}, [products]);

	// ── Navigation handlers ──────────────────────────────────────────────────────

	const {
		goToBuildingModel,
		goToTierSection, //isn't this normal app navigation? TODO: check!
	} = useHeroSectionNavigation({
		products,
		onAfterNavigate: previewHandlersRef.current.handleClosePreview,
	});

	// ── CTA and instruction logic ─────────────────────────────────────────────
	// Determine which model view to navigate to when CTA is clicked
	const canShowCta =
		heroCapabilities.hasSiteModel || heroCapabilities.hasBuildingModel;
	const targetModelView = heroCapabilities.hasSiteModel
		? HERO_VIEW.SITE
		: HERO_VIEW.BUILDING;

	const handleCtaMouseEnter = useCallback(() => {
		setHasCtaHoverArmed(true);
		setIsCtaExpanded(true);
	}, []);

	const handleCtaMouseLeave = useCallback(() => {
		if (!hasCtaHoverArmed) return;
		setIsCtaExpanded(false);
	}, [hasCtaHoverArmed]);

	// ── Interaction hooks ─────────────────────────────────────────────────────────────────────────────────────────────
	// main.svg has all three layers (buildings, tiers, apartments) in one SVG.
	// Unlike ModelView (separate outline files), ALL layers are wired at once.
	//
	// Click behavior:
	//   Building  → setActiveBuilding + switch to BUILDING view (no scroll)
	//   Tier      → setActiveTier + setActiveBuilding + scroll to TiersView
	//   Apartment → selectProductHandler (sets product+building+tier + scrolls to ProductView)

	const deviceLayout = useMemo(() => ({ isMobile }), [isMobile]);

	const { wireListeners: buildingWireListeners } = useSvgBuildingInteraction({
		areSoldOutBuildingsActive,
		buildingsData: buildings ?? [],
		activeBuilding,
		onOutlineClick: handleOutlineAction,
		onPreviewChange: handlePreviewChange,
		deviceLayout,
		soldUnitsAccessible,
	});

	const { wireListeners: tierWireListeners } = useSvgTierInteraction({
		areSoldOutBuildingsActive,
		areSoldOutTiersActive,
		tiersData: tiers ?? [],
		buildingsData: buildings ?? [],
		activeTier,
		activeBuilding,
		onOutlineClick: handleOutlineAction,
		onPreviewChange: handlePreviewChange,
		deviceLayout,
		soldUnitsAccessible,
	});

	const { wireListeners: productWireListeners } = useSvgProductInteraction({
		products: products ?? [],
		filteredProducts: filteredProducts ?? [],
		buildings,
		activeProduct,
    colorMode,
    colorModeDeciles,
		onProductClick: handleOutlineAction,
		onPreviewChange: handlePreviewChange,
		areSoldAccessible,
		areReservedAccessible,
		highlightSoldInRed: heroCapabilities.highlightSoldInRed,
		deviceLayout,
	});

	// ── Cleanup Effects ───────────────────────────────────────────────────────────────

	useEffect(() => {
		if (isMobile || !canShowCta) {
			setIsCtaExpanded(false);
			setHasCtaHoverArmed(false);
			return;
		}

		setIsCtaExpanded(false);
		setHasCtaHoverArmed(false);

		const timeoutId = window.setTimeout(() => {
			setIsCtaExpanded(true);
		}, CTA_ONBOARDING_DELAY_MS);

		return () => window.clearTimeout(timeoutId);
	}, [canShowCta, isMobile]);

	// Normalize integer path IDs → canonical format before hooks run
	useEffect(() => {
		if (visSvgStatus !== FETCH_STATUS.READY || !visSvgRef.current) return;
		normalizeVisPathIds(visSvgRef.current, {
			buildings: buildings ?? [],
			tiers: tiers ?? [],
		});
		const ids = Array.from(visSvgRef.current.querySelectorAll('path[id]')).map(
			(el) => el.id,
		);
		const classified = classifyVisPathIds(ids, {
			buildings: buildings ?? [],
			tiers: tiers ?? [],
		});
		const present = new Set(classified.values());
		const ordered = [
			HERO_OUTLINE.APARTMENTS,
			HERO_OUTLINE.TIERS,
			HERO_OUTLINE.BUILDINGS,
		].filter((layer) => present.has(layer));
		setDetectedOutlineLayers(ordered);
	}, [visSvgString, visSvgStatus, buildings, tiers]);

	// ── Interaction wire ─────────────────────────────────────────────────────────

	// Wire ALL interaction layers simultaneously.
	// main.svg embeds buildings, tiers, and apartments in one flat SVG —
	// unlike ModelView where each outline is a separate file toggled by activeOutline.
	useEffect(() => {
		if (visSvgStatus !== FETCH_STATUS.READY || !visSvgRef.current) return;

		const cleanups = [];

		if (buildingWireListeners)
			cleanups.push(buildingWireListeners(visSvgRef.current));
		if (tierWireListeners) cleanups.push(tierWireListeners(visSvgRef.current));
		if (productWireListeners)
			cleanups.push(productWireListeners(visSvgRef.current));

		return () => cleanups.forEach((fn) => fn?.());
	}, [
		visSvgString,
		visSvgStatus,
		buildingWireListeners,
		tierWireListeners,
		productWireListeners,
	]);

	// ── Render ────────────────────────────────────────────────────────────────────────────────────────────────────────

	// ── Render helpers and values ─────────────────────────────────────────────

	// Determine instruction text from the categories actually present in main.svg.
	// Priority = deepest actionable layer first.
	const primaryDetectedLayer = detectedOutlineLayers[0] ?? null;
	let instructionText = t('hero.select_building');
	if (primaryDetectedLayer === HERO_OUTLINE.APARTMENTS) {
		instructionText = getProductsCategoryString('select', 'default');
	} else if (primaryDetectedLayer === HERO_OUTLINE.TIERS) {
		instructionText = t('hero.select_floor');
	} else if (primaryDetectedLayer === HERO_OUTLINE.BUILDINGS) {
		instructionText = t('hero.select_building');
	}

	if (visSvgStatus === FETCH_STATUS.LOADING) {
		return <div className={styles.loading} />;
	}
	if (visSvgStatus === FETCH_STATUS.ERROR || !visSvgString) {
		return null;
	}
	return (
		<div className={styles.visualizationWrapper}>
			<div
				ref={visSvgRef}
				className={styles.visSvg}
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: visSvgString }}
			/>

			{/* ── CTA button "See 3D Model" ── */}
			<div className={styles.topBar}>
				{canShowCta && (
					// <ButtonHero
					// 	aria-label={t('hero.open_three_d_model')}
					// 	className={[
					// 		styles.ctaButton,
					// 		isCtaExpanded
					// 			? styles.ctaButtonExpanded
					// 			: styles.ctaButtonCollapsed,
					// 	].join(' ')}
					// 	onClick={() => changeViewHandler(targetModelView)}
					// 	onMouseEnter={handleCtaMouseEnter}
					// 	onMouseLeave={handleCtaMouseLeave}
					// 	onFocus={handleCtaMouseEnter}
					// 	onBlur={handleCtaMouseLeave}
					// >
					// 	<span className={styles.ctaText}>
					// 		{t('hero.open_three_d_model')}
					// 	</span>
					// 	<ThreeDModel
					// 		className={styles.ctaIcon}
					// 		arrowsClassName={styles.ctaIconArrows}
					// 	/>
					// </ButtonHero>
					<HeroCtaButton 
						text={t('hero.open_three_d_model')}
						// Icon={(props) => (
						// 	<ThreeDModel {...props} //arrowsClassName={styles.rotatingArrows} />
						// 		arrowsClassName={styles.ctaIconArrows}
						// 	/>
						// )} 
						Icon={({ className, isExpanded }) => (
								<ThreeDModel 
										className={className} 
										arrowsClassName={isExpanded ? styles.arrowsSpin : styles.arrowsIdle} 
								/>
						)}
						onClick={() => changeViewHandler(targetModelView)} 
					/>
				)}
			</div>

			{/* ── Instruction box (bottom-right) ── */}
			<div className={styles.bottomBar}>
				<div className={styles.instructionBox}>
					<div className={styles.instructionText}>{instructionText}</div>
				</div>
			</div>

			{/* ── Outline preview (building / tier / apartments hover) ───────────────────────── */}
			<PreviewOverlay
				activeOutline={primaryDetectedLayer}
				currentIndexRef={null}
				getMobileOutlineCandidates={getVisOutlineCandidates}
				getMobileProductCandidates={getVisProductCandidates}
				goToBuildingModel={goToBuildingModel}
				goToTierSection={goToTierSection}
				svgContainerRef={visSvgRef}
				onRegisterPreviewHandler={handleRegisterPreviewHandler}
			/>
		</div>
	);
}
